/**
 * Marketplace facet filters (loan-syndication-protocol 1.2.0, GH #4669).
 *
 * Every facet the server counts (GET /marketplace/facets) is also a browse
 * filter on GET /marketplace, and the two share one key set: a bucket's
 * value is a value the filter parameter accepts. This module turns the
 * facets aggregate into FilterBar definitions and the page's active filters
 * into the browse query, so the server filters before it paginates and a
 * chosen facet describes the whole network rather than one fetched page.
 *
 * FilterBar holds one value per filter, so each parameter is sent as a
 * one-element array. The contract accepts up to the full key set per
 * parameter (OR within a parameter, AND across parameters).
 */

import type { ActiveFilter, FilterDefinition } from '@aaimplatform/ui';
import type {
  components as LspComponents,
  operations as LspOperations,
} from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { ASSET_CLASS_LABEL } from '../pledges/pledge-display';
import { LOAN_TYPE_LABELS } from './format';

type Schemas = LspComponents['schemas'];
type MarketplaceFacets = Schemas['MarketplaceFacets'];
type FacetBucket = Schemas['FacetBucket'];
type BrowseQuery = NonNullable<LspOperations['browseMarketplace']['parameters']['query']>;

/** The browse parameters that are facet filters (status keeps its own control). */
// adhoc-type-skip: an Omit of the generated operations['browseMarketplace'] query type; no independent shape
export type MarketplaceFacetQuery = Omit<
  BrowseQuery,
  'status' | 'minAmount' | 'eligibleOnly' | 'page' | 'pageSize'
>;

type Unknown = LspComponents['schemas']['MarketplaceFacetUnknown'];

/** Label for UNKNOWN on a dimension the lead declares when it lists. */
const NOT_DECLARED = 'Not declared';

// ---------------------------------------------------------------------------
// Label maps. Each is keyed by the contract's union, so tsc fails when the
// spec adds a key the map does not label; each also serves as the accept
// list when a FilterBar value is read back into the query.
// ---------------------------------------------------------------------------

export const REGULATORY_FRAMEWORK_LABELS: Record<Schemas['RegulatoryFramework'], string> = {
  NCUA_701_22: 'NCUA 701.22',
  OCC_12_CFR_32: 'OCC 12 CFR 32',
  FDIC: 'FDIC',
  STATE: 'State-chartered',
  OTHER: 'Other',
};

export const RATE_TYPE_LABELS: Record<Schemas['LoanDetails']['rateType'], string> = {
  FIXED: 'Fixed',
  VARIABLE: 'Variable',
};

export const AMOUNT_BAND_LABELS: Record<Schemas['MarketplaceAmountBand'], string> = {
  LT_250K: 'Under $250K',
  '250K_1M': '$250K-$1M',
  '1M_5M': '$1M-$5M',
  '5M_25M': '$5M-$25M',
  GTE_25M: '$25M+',
};

const RATE_BAND_LABELS: Record<Schemas['MarketplaceRateBand'], string> = {
  LT_0_04: 'Under 4%',
  '0_04_0_06': '4% to under 6%',
  '0_06_0_08': '6% to under 8%',
  '0_08_0_10': '8% to under 10%',
  GTE_0_10: '10% and above',
};

const TERM_BAND_LABELS: Record<Schemas['MarketplaceTermBand'], string> = {
  LTE_12M: '12 months or less',
  '13_36M': '13 to 36 months',
  '37_60M': '37 to 60 months',
  '61_120M': '61 to 120 months',
  GT_120M: 'Over 120 months',
};

/** LTV bands are right-closed, as LTV limits are written. */
const LTV_BAND_LABELS: Record<Schemas['MarketplaceLtvBand'], string> = {
  LTE_0_50: '50% or less',
  '0_50_0_65': 'Over 50% to 65%',
  '0_65_0_80': 'Over 65% to 80%',
  GT_0_80: 'Over 80%',
  UNKNOWN: 'No collateral pinned',
};

const ASSET_CLASS_LABELS: Record<Schemas['AssetClass'] | Unknown, string> = {
  ...ASSET_CLASS_LABEL,
  UNKNOWN: 'No collateral pinned',
};

const DEADLINE_WINDOW_LABELS: Record<Schemas['MarketplaceDeadlineWindow'], string> = {
  PASSED: 'Deadline passed',
  WITHIN_7D: 'Within 7 days',
  WITHIN_30D: '7 to 30 days',
  LATER: '30 days or more',
};

const DISTRIBUTION_WATERFALL_LABELS: Record<Schemas['DistributionWaterfall'], string> = {
  PRO_RATA: 'Pro rata',
  SEQUENTIAL: 'Sequential',
};

const US_STATE_LABELS: Record<Schemas['USStateCode'] | Unknown, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  AS: 'American Samoa',
  GU: 'Guam',
  MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico',
  VI: 'U.S. Virgin Islands',
  UNKNOWN: NOT_DECLARED,
};

const CENSUS_REGION_LABELS: Record<Schemas['CensusRegion'] | Unknown, string> = {
  NORTHEAST: 'Northeast',
  MIDWEST: 'Midwest',
  SOUTH: 'South',
  WEST: 'West',
  UNKNOWN: NOT_DECLARED,
};

const CHARTER_TYPE_LABELS: Record<Schemas['CharterType'] | Unknown, string> = {
  NATIONAL_BANK: 'National bank',
  STATE_MEMBER_BANK: 'State member bank',
  STATE_NONMEMBER_BANK: 'State nonmember bank',
  SAVINGS_BANK: 'Savings bank',
  SAVINGS_ASSOCIATION: 'Savings association',
  FEDERAL_CREDIT_UNION: 'Federal credit union',
  STATE_CREDIT_UNION: 'State credit union',
  UNKNOWN: NOT_DECLARED,
};

const FICO_BAND_LABELS: Record<Schemas['MarketplaceFicoBand'], string> = {
  LT_580: 'Under 580',
  '580_669': '580 to 669',
  '670_739': '670 to 739',
  '740_799': '740 to 799',
  GTE_800: '800 and above',
  UNKNOWN: NOT_DECLARED,
};

const DSCR_BAND_LABELS: Record<Schemas['MarketplaceDscrBand'], string> = {
  LT_1_00: 'Under 1.00x',
  '1_00_1_25': '1.00x to under 1.25x',
  '1_25_1_50': '1.25x to under 1.50x',
  '1_50_2_00': '1.50x to under 2.00x',
  GTE_2_00: '2.00x and above',
  UNKNOWN: NOT_DECLARED,
};

const SETTLEMENT_METHOD_LABELS: Record<Schemas['SettlementMethod'] | Unknown, string> = {
  WIRE: 'Wire',
  ACH: 'ACH',
  CRT: 'CRT',
  UNKNOWN: NOT_DECLARED,
};

/** A five-digit CBSA code, the geographyMsa filter's pattern. */
const CBSA_CODE = /^\d{5}$/;
const MSA_LABELS: Record<Unknown, string> = { UNKNOWN: NOT_DECLARED };

// ---------------------------------------------------------------------------
// FilterBar definitions
// ---------------------------------------------------------------------------

/** The 18 facet filters in FilterBar order: the 1.1 five, then the 1.2.0 thirteen. */
export const FACET_FILTER_IDS = [
  'regulatoryFramework',
  'rateType',
  'loanType',
  'amountBucket',
  'rateBand',
  'termBand',
  'ltvBand',
  'remainingCapacityBand',
  'assetClass',
  'commitmentDeadlineWindow',
  'distributionWaterfall',
  'geographyState',
  'geographyMsa',
  'geographyRegion',
  'originatorCharterType',
  'ficoBand',
  'dscrBand',
  'settlementMethodsAccepted',
] as const satisfies readonly (keyof MarketplaceFacetQuery & keyof MarketplaceFacets)[];

type FacetFilterId = (typeof FACET_FILTER_IDS)[number];
/** Compile-time check: a browse filter the list leaves out fails here. */
const COVERS_EVERY_FILTER: Exclude<keyof MarketplaceFacetQuery, FacetFilterId> extends never ? true : never = true;
void COVERS_EVERY_FILTER;

const FACET_FILTERS: Record<FacetFilterId, { label: string; labels: Readonly<Record<string, string>> }> = {
  regulatoryFramework: { label: 'Regulatory framework', labels: REGULATORY_FRAMEWORK_LABELS },
  rateType: { label: 'Rate type', labels: RATE_TYPE_LABELS },
  loanType: { label: 'Asset type', labels: LOAN_TYPE_LABELS },
  amountBucket: { label: 'Deal size', labels: AMOUNT_BAND_LABELS },
  rateBand: { label: 'Interest rate', labels: RATE_BAND_LABELS },
  termBand: { label: 'Term', labels: TERM_BAND_LABELS },
  ltvBand: { label: 'Loan-to-value', labels: LTV_BAND_LABELS },
  remainingCapacityBand: { label: 'Remaining capacity', labels: AMOUNT_BAND_LABELS },
  assetClass: { label: 'Collateral class', labels: ASSET_CLASS_LABELS },
  commitmentDeadlineWindow: { label: 'Commitment deadline', labels: DEADLINE_WINDOW_LABELS },
  distributionWaterfall: { label: 'Distribution waterfall', labels: DISTRIBUTION_WATERFALL_LABELS },
  geographyState: { label: 'State', labels: US_STATE_LABELS },
  geographyMsa: { label: 'Metro area (CBSA)', labels: MSA_LABELS },
  geographyRegion: { label: 'Census region', labels: CENSUS_REGION_LABELS },
  originatorCharterType: { label: 'Lead charter', labels: CHARTER_TYPE_LABELS },
  ficoBand: { label: 'Weighted average FICO', labels: FICO_BAND_LABELS },
  dscrBand: { label: 'Weighted average DSCR', labels: DSCR_BAND_LABELS },
  settlementMethodsAccepted: { label: 'Settlement method', labels: SETTLEMENT_METHOD_LABELS },
};

/** The five dimensions every facets response carries; the rest are optional in the contract. */
const ALWAYS_OFFERED: ReadonlySet<FacetFilterId> = new Set([
  'regulatoryFramework',
  'rateType',
  'loanType',
  'amountBucket',
]);

const STATUS_LABELS: Record<'OPEN' | 'FULLY_COMMITTED', string> = {
  OPEN: 'Open',
  FULLY_COMMITTED: 'Fully committed',
};

/** A bucket's option: its label and its network-wide count. A CBSA code prints as the code. */
export function facetOptions(
  buckets: readonly FacetBucket[] | undefined,
  labels: Readonly<Record<string, string>>,
): Array<{ value: string; label: string }> {
  return (buckets ?? []).map((b) => ({ value: b.value, label: `${labels[b.value] ?? b.value} (${b.count})` }));
}

/**
 * FilterBar definitions from the live facets aggregate: status and the four
 * dimensions the 1.1 contract always counted are always offered (empty until
 * the aggregate loads); a 1.2.0 dimension is offered once the aggregate
 * carries at least one bucket for it.
 */
export function buildFacetFilterDefinitions(facets: MarketplaceFacets | undefined): FilterDefinition[] {
  const definitions: FilterDefinition[] = [
    { id: 'status', label: 'Status', type: 'select', options: facetOptions(facets?.status, STATUS_LABELS) },
  ];
  for (const id of FACET_FILTER_IDS) {
    const buckets = facets?.[id];
    if (!ALWAYS_OFFERED.has(id) && (buckets === undefined || buckets.length === 0)) continue;
    const { label, labels } = FACET_FILTERS[id];
    definitions.push({ id, label, type: 'select', options: facetOptions(buckets, labels) });
  }
  return definitions;
}

// ---------------------------------------------------------------------------
// Active filters -> browse query
// ---------------------------------------------------------------------------

function isLabeledKey<K extends string>(labels: Readonly<Record<K, string>>, value: string): value is K {
  return Object.prototype.hasOwnProperty.call(labels, value);
}

/**
 * One filter's value as a one-element parameter array, or undefined when the
 * filter is not active or its value is not a key the parameter accepts (a
 * value the contract would reject never reaches the request).
 */
function pick<K extends string>(
  active: readonly ActiveFilter[],
  id: FacetFilterId,
  labels: Readonly<Record<K, string>>,
): K[] | undefined {
  const value = active.find((f) => f.filterId === id)?.value;
  return value !== undefined && isLabeledKey(labels, value) ? [value] : undefined;
}

function pickMsa(active: readonly ActiveFilter[]): MarketplaceFacetQuery['geographyMsa'] {
  const value = active.find((f) => f.filterId === 'geographyMsa')?.value;
  if (value === undefined) return undefined;
  if (isLabeledKey(MSA_LABELS, value)) return [value];
  return CBSA_CODE.test(value) ? [value] : undefined;
}

/** The browse query's facet parameters for the page's active filters. */
export function facetQueryFromFilters(active: readonly ActiveFilter[]): MarketplaceFacetQuery {
  const query: MarketplaceFacetQuery = {
    regulatoryFramework: pick(active, 'regulatoryFramework', REGULATORY_FRAMEWORK_LABELS),
    rateType: pick(active, 'rateType', RATE_TYPE_LABELS),
    loanType: pick(active, 'loanType', LOAN_TYPE_LABELS),
    amountBucket: pick(active, 'amountBucket', AMOUNT_BAND_LABELS),
    rateBand: pick(active, 'rateBand', RATE_BAND_LABELS),
    termBand: pick(active, 'termBand', TERM_BAND_LABELS),
    ltvBand: pick(active, 'ltvBand', LTV_BAND_LABELS),
    remainingCapacityBand: pick(active, 'remainingCapacityBand', AMOUNT_BAND_LABELS),
    assetClass: pick(active, 'assetClass', ASSET_CLASS_LABELS),
    commitmentDeadlineWindow: pick(active, 'commitmentDeadlineWindow', DEADLINE_WINDOW_LABELS),
    distributionWaterfall: pick(active, 'distributionWaterfall', DISTRIBUTION_WATERFALL_LABELS),
    geographyState: pick(active, 'geographyState', US_STATE_LABELS),
    geographyMsa: pickMsa(active),
    geographyRegion: pick(active, 'geographyRegion', CENSUS_REGION_LABELS),
    originatorCharterType: pick(active, 'originatorCharterType', CHARTER_TYPE_LABELS),
    ficoBand: pick(active, 'ficoBand', FICO_BAND_LABELS),
    dscrBand: pick(active, 'dscrBand', DSCR_BAND_LABELS),
    settlementMethodsAccepted: pick(active, 'settlementMethodsAccepted', SETTLEMENT_METHOD_LABELS),
  };
  // Drop unset parameters so the query key and the request carry only what applies.
  for (const id of FACET_FILTER_IDS) {
    if (query[id] === undefined) delete query[id];
  }
  return query;
}
